import { useState } from "react";
import { BusinessCategory } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { submitBusiness } from "@/services/business/submissionService";
import { validateBusinessForm } from "@/utils/formValidation";

interface FormData {
  name: string;
  description: string;
  address: string;
  city: string;
  state: string;
  postcode: string;
  what3words: string;
  category: BusinessCategory | "";
  tags: string;
  website: string;
  phone: string;
  email: string;
  mediaFiles: File[];
  facebook: string;
  instagram: string;
  tiktok: string;
}

interface FormErrors {
  [key: string]: string;
}

export const useSubmitForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    description: "",
    address: "",
    city: "",
    state: "",
    postcode: "",
    what3words: "",
    category: "",
    tags: "",
    website: "",
    phone: "",
    email: "",
    mediaFiles: [],
    facebook: "",
    instagram: "",
    tiktok: "",
  });
  
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCategoryChange = (value: string) => {
    setFormData((prev) => ({ ...prev, category: value as BusinessCategory }));
    if (formErrors.category) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.category;
        return newErrors;
      });
    }
  };

  const handleMediaChange = (files: FileList) => {
    console.log("Media files selected:", Array.from(files).map(f => f.name));
    const fileArray = Array.from(files);
    
    const combinedFiles = [...formData.mediaFiles, ...fileArray];
    const limitedFiles = combinedFiles.slice(0, 10);
    
    setFormData((prev) => ({ ...prev, mediaFiles: limitedFiles }));
    
    if (combinedFiles.length > 10) {
      toast({
        title: "Too many files",
        description: `Only the first 10 files were selected. Maximum of 10 files allowed.`,
        variant: "default",
      });
    }
    
    if (formErrors.mediaFiles) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.mediaFiles;
        return newErrors;
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      address: "",
      city: "",
      state: "",
      postcode: "",
      what3words: "",
      category: "",
      tags: "",
      website: "",
      phone: "",
      email: "",
      mediaFiles: [],
      facebook: "",
      instagram: "",
      tiktok: "",
    });
    setFormErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setFormErrors({});
    
    console.log('Form Submission Started', { formData });
    
    const validation = validateBusinessForm(formData);
    
    if (!validation.isValid) {
      console.error('Form Validation Failed', validation.errors);
      setFormErrors(validation.errors);
      
      toast({
        title: "Validation Error",
        description: "Please check the form and try again",
        variant: "destructive"
      });
      
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('Preparing Business Submission', {
        name: formData.name,
        category: formData.category,
        mediaFiles: formData.mediaFiles?.length || 0
      });
      
      const result = await submitBusiness({
        name: formData.name,
        description: formData.description,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        category: formData.category as BusinessCategory,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        website: formData.website || null,
        phone: formData.phone || null,
        email: formData.email || null,
        mediaFiles: formData.mediaFiles.length > 0 ? formData.mediaFiles : undefined,
        facebook: formData.facebook || null,
        instagram: formData.instagram || null,
        tiktok: formData.tiktok || null,
      });
      
      console.log('Submission Successful', result);
      
      toast({
        title: "Success",
        description: "Business listing submitted successfully"
      });
      
      resetForm();
      navigate("/");
    } catch (error) {
      console.error('Submission Error', error);
      
      const errorMessage = error instanceof Error 
        ? error.message 
        : "An unexpected error occurred";
      
      toast({
        title: "Submission Failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    formErrors,
    isSubmitting,
    setFormErrors,
    setIsSubmitting,
    handleChange,
    handleCategoryChange,
    handleMediaChange,
    resetForm,
    toast,
    navigate,
    handleSubmit
  };
};
